import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
// components
import {
	TextInput,
	TextArea,
	DatePicker,
	DatePickerInput,
	Toggle,
} from 'carbon-components-react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import SelectLabel from '../../../components/SelectLabel'
import FieldGroupArray from './detailConneX/FieldGroupArray'
// hooks
import useAgeField from '../../../helpers/useAgeFields'
// constants
import {
	getRecruitStatusOptions,
	getSexOptions,
	getTrialPhaseOptions,
	getTrialTypeOptions,
} from '../../../constants/connex'
// styles
import {
	selectStyles,
	getSelectValuesToSubmit,
	getValuesForReactSelect,
} from '../../../helpers/formUtils'
import './DetailConneX.scss';

const DetailConneX = ({
	data,
	getFieldValue,
	dirtyFields,
	dataToSubmit,
	setDataToSubmit,
	SET_OVERRIDE_DATA,
	conditionOptions,
	interventionOptions,
}) => {
	const onlyAttachment = localStorage.getItem('only_attachment')

	// add id with index number to data
	const addIdsToDataArray = (dataArray) => {
		if (dataArray) {
			return dataArray.map((item, index) => {
				let newItem = { ...item }
				newItem.orderId = index + 1
				newItem.id = uuidv4()
				return newItem
			})
		}
		return null
	}

	// fields defining array fields

	const trialArmsFields = [
		{
			id: "description",
			labelText: "Description",
			type: "textArea",
			rows: 3,
		},
		{
			id: "arm_group_label",
			labelText: "Label",
			type: "textArea",
			rows: 1,
		},
		{
			id: "arm_group_type",
			labelText: "Type",
			type: "textInput",
		},
	]

	const outcomeMeasureFields = [
		{
			id: "description",
			labelText: "Description",
			type: "textArea",
			rows: 3,
		},
		{
			id: "measure",
			labelText: "Measure",
			type: "textArea",
			rows: 2,
		},
		{
			id: "time_frame",
			labelText: "Time Frame",
			type: "textInput",
		},
	]

	const onChangeAge = (name, value) => {
		setDataToSubmit({ type: SET_OVERRIDE_DATA, name, value })
	}

	const {
		numberInput,
		unitsSelect,
		ages,
	} = useAgeField((() => {
		let defaultState = null;
		['maximum_age', 'minimum_age'].forEach((field) => {
			if (data?.override?.[field]) {
				defaultState = defaultState || {}
				defaultState[field] = { ...data.override[field] }
			}
		})
		return defaultState
	})(), dirtyFields?.override, onChangeAge)


	/** EVENT HANDLERS */

	const onTextFieldChange = (evt) => {
		const id = evt.target.id
		let value = evt.target.value
		setDataToSubmit({ type: SET_OVERRIDE_DATA, name: evt.target.id, value })
	}

	const onDateFieldChange = (id, date) => {
		const value = date?.length ? moment(date[0]).toISOString() : null
		setDataToSubmit({
			type: SET_OVERRIDE_DATA,
			name: id,
			value,
		})
	}

	const onAcceptsVolunteers = (value, id, evt) => {
		const submitVal = value ? 'Yes' : 'No'
		setDataToSubmit({
			type: SET_OVERRIDE_DATA,
			name: id,
			value: submitVal,
		})
	}

	const onSelectChange = (selectedItems, evtObject) => {
		const name = evtObject.name
		const value = getSelectValuesToSubmit(selectedItems, evtObject)
		if (name) {
			setDataToSubmit({ type: SET_OVERRIDE_DATA, name, value })
		}
	}


	/** RENDERERS */

	const fieldHeaderRow = () => {
		return <div className="detail-connex__panel-body" style={{ paddingBottom: 0 }}>
			<div className="detail-connex__column-header">Override values</div>
			<div className="detail-connex__column-header">Original values</div>
		</div>
	}

	const getTextRow = (type, id, label, moreProps) => {
		const propsAll = {
			labelText: label,
			size: 'sm',
		}
		const overrideProps = {
			id,
			defaultValue: getFieldValue(id, 'override'),
			onChange: onTextFieldChange,
			onInput: onTextFieldChange,
			disabled: onlyAttachment === "true"
		}
		const origProps = {
			id: `orig_${id}`,
			value: getFieldValue(id, 'ctg_data'),
			readOnly: true,
			disabled: !!data?.override?.[id],
			onFocus: (evt) => evt.target.select(),
		}
		return <div className="detail-connex__panel-body" style={{ paddingTop: 0, paddingBottom: 0 }}>
			<div className="detail-connex__field">
				<div className={`detail-connex__override-field${dirtyFields?.override?.[id] ? ' detail-connex__override-field--dirty' : ''}`}>
					{type === 'textInput' ?
						<TextInput
							{...propsAll}
							{...overrideProps}
							{...moreProps}
						/> :
						<TextArea
							{...propsAll}
							{...overrideProps}
							{...moreProps}
						/>
					}
				</div>
			</div>
			<div className="detail-connex__field">
				<div className="detail-connex__orig-field">
					{type === 'textInput' ?
						<TextInput
							{...propsAll}
							{...origProps}
							{...moreProps}
						/> :
						<TextArea
							{...propsAll}
							{...origProps}
							{...moreProps}
						/>
					}
				</div>
			</div>
		</div>
	}

	const getDatePickerRow = (fieldId, label) => {
		let origValue = getFieldValue(fieldId, 'ctg_data')
		if (origValue) {
			origValue = moment(origValue).format('MM/DD/YYYY')
		} else {
			origValue = ''
		}
		let overrideValue = getFieldValue(fieldId, 'override')
		if (overrideValue) {
			overrideValue = moment(overrideValue).format('MM/DD/YYYY')
		} else {
			overrideValue = ''
		}
		return <div className="detail-connex__panel-body" style={{ paddingTop: 0, paddingBottom: 0 }}>
			<div className="detail-connex__field">
				<div className={`detail-connex__override-date${dirtyFields?.override?.[fieldId] ? ' detail-connex__override-date--dirty' : ''}`}>
					<DatePicker
						datePickerType="single"
						dateFormat="m/d/Y"
						onChange={(date) => onDateFieldChange(fieldId, date)}
					>
						<DatePickerInput
							id={fieldId}
							placeholder="mm/dd/yyyy"
							labelText={label}
							disabled={onlyAttachment === "true"}
							size="sm"
							defaultValue={overrideValue}
						/>
					</DatePicker>
				</div>
			</div>
			<div className="detail-connex__field">
				<div className="detail-connex__orig-field">
					<TextInput
						id={`orig_${fieldId}`}
						labelText={label}
						value={origValue}
						size="sm"
						readOnly={true}
						onFocus={(evt) => evt.target.select()}
						disabled={overrideValue}
					/>
				</div>
			</div>
		</div>
	}

	const getSelectRow = (
		id,
		label,
		selectOptions,
		isMulti = false,
	) => {
		const defaultOverrideValue = getValuesForReactSelect(data?.override?.[id], selectOptions)
		const isOverriden = defaultOverrideValue?.length > 0

		const originalValues = (val) => {
			let displayVal = null
			if (!val) {
				displayVal = <>&nbsp;</>
			} else if (Array.isArray(val) && val?.length) {
				displayVal = val.map((text) => <span
					key={text}
					className={`detail-connex__orig-value-tag${isOverriden ? ' detail-connex__orig-value-tag--overridden' : ''}`}
				>{text}</span>)
			} else {
				displayVal = <span className="detail-connex__orig-select-value">{val}</span>
			}
			return <>{displayVal}</>
		}

		const getArrayOfStrings = (items) => {
			if (items?.length) {
				return items.map((item) => (item.name))
			}
		}

		return <div className="detail-connex__panel-body" style={{ paddingTop: 0, paddingBottom: 0 }}>
			<div className="detail-connex__field">
				<SelectLabel id={id}>{label}</SelectLabel>
				<Select
					name={id}
					aria-labelledby={id}
					defaultValue={defaultOverrideValue}
					options={selectOptions}
					isMulti={isMulti}
					onChange={onSelectChange}
					styles={selectStyles({ isDirty: dirtyFields?.override?.[id] })}
					isDisabled={onlyAttachment === "true"}
					isClearable={true}
				/>
			</div>
			<div className="detail-connex__field">
				<div className={`detail-connex__orig-select${isOverriden ? ' detail-connex__orig-select--overridden' : ''}`}>
					<label className="bx--label">
						{label}
					</label>
					<div className="detail-connex__orig-select-inner">
						{isMulti ?
						originalValues(getArrayOfStrings(getFieldValue(id)))
						:
						originalValues(getFieldValue(id, 'ctg_data'))
						}
					</div>
				</div>
			</div>
		</div>
	}

	const getSelectCreatableRow = (
		id,
		label,
		selectOptions,
		isMulti = false,
	) => {
		let defaultOverrideValue;
		if(id === "conditions"){
			defaultOverrideValue = getValuesForReactSelect(data?.conditions_override, selectOptions);
		}
		else if(id === "interventions"){
			defaultOverrideValue = getValuesForReactSelect(data?.interventions_override, selectOptions);
		}
		else{
			defaultOverrideValue = getValuesForReactSelect(data?.override?.[id], selectOptions);
		}

		const isOverriden = defaultOverrideValue?.length > 0

		const originalValues = (val) => {
			let displayVal = null
			if (!val) {
				displayVal = <>&nbsp;</>
			} else if (Array.isArray(val) && val?.length) {
				displayVal = val.map((text) => <span
					key={text}
					className={`detail-connex__orig-value-tag${isOverriden ? ' detail-connex__orig-value-tag--overridden' : ''}`}
				>{text}</span>)
			} else {
				displayVal = <span className="detail-connex__orig-select-value">{val}</span>
			}
			return <>{displayVal}</>
		}

		const getArrayOfStrings = (items) => {
			if (items?.length) {
				return items.map((item) => (item.name))
			}
		}

		return <div className="detail-connex__panel-body" style={{ paddingTop: 0, paddingBottom: 0 }}>
			<div className="detail-connex__field">
				<SelectLabel id={id}>{label}</SelectLabel>
				<CreatableSelect
					isDisabled={onlyAttachment === "true"}
					name={id}
					aria-labelledby={id}
					defaultValue={defaultOverrideValue}
					options={selectOptions}
					isMulti={isMulti}
					onChange={onSelectChange}
					styles={selectStyles({ isDirty: dirtyFields?.override?.[id] })}
					isClearable={true}
				/>
			</div>
			<div className="detail-connex__field">
				<div className={`detail-connex__orig-select${isOverriden ? ' detail-connex__orig-select--overridden' : ''}`}>
					<label className="bx--label">
						{label}
					</label>
					<div className="detail-connex__orig-select-inner">
						{isMulti ?
						originalValues(getArrayOfStrings(getFieldValue(id)))
						:
						originalValues(getFieldValue(id, 'ctg_data'))
						}
					</div>
				</div>
			</div>
		</div>
	}

	const getSelectAge = (id, label) => {
		return <div className="detail-connex__panel-body" style={{ paddingTop: 0, paddingBottom: 0 }}>
			<div className="detail-connex__field" style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
				<div style={{ flex: '0 0 auto' }}>
					{numberInput(id, label)}
				</div>
				<div style={{ flex: 1 }}>
					{unitsSelect(id)}
				</div>
			</div>
			<div className="detail-connex__field">
				<div className={`detail-connex__orig-select${data?.override?.[id] ? ' detail-connex__orig-select--overridden' : ''}`}>
					<label className="bx--label">
						{label}
					</label>
					<div className="detail-connex__orig-select-inner">
						<span className="detail-connex__orig-select-value">
							{getFieldValue(id, 'ctg_data')}
						</span>
					</div>
				</div>
			</div>
		</div>
	}

	const getToggle = (id) => {
		const defaultToggled = data?.override?.[id] === 'Yes'

		const acceptsVolunteers = () => {
			if (data?.ctg_data?.[id]) {
				const accepts = String(data.ctg_data[id]).toLowerCase()
				if (accepts === 'no') {
					return false
				} else if (accepts === 'yes') {
					return true
				}
			}
			return null
		}

		return <div className="detail-connex__panel-body" style={{ paddingTop: 0, paddingBottom: 0 }}>
			<div className="detail-connex__field">
				<div className={`detail-connex__override-toggle${dirtyFields?.override?.[id] ? ' detail-connex__override-toggle--dirty' : ''}`}>
					<Toggle
						id={id}
						labelA="Does Not Accept Healthy Volunteers"
						labelB="Accepts Healthy Volunteers"
						onToggle={onAcceptsVolunteers}
						disabled={onlyAttachment === "true"}
						defaultToggled={defaultToggled}
					/>
				</div>
			</div>
			<div className="detail-connex__field">
				{(acceptsVolunteers || acceptsVolunteers === false) ?
					<div className={`detail-connex__orig-toggle${data?.override?.[id] ? ' detail-connex__orig-toggle--overridden' : ''}`}>
						<Toggle
							id={`orig_${id}`}
							labelA="Does Not Accept Healthy Volunteers"
							labelB="Accepts Healthy Volunteers"
							defaultToggled={acceptsVolunteers()}
							disabled={true}
						/>
					</div>
					: <div>No answer</div>}
			</div>
		</div>
	}

	const getFieldGroupArray = (id, title, fields) => {
		const overrideData = data?.override?.[id]
		const origData = addIdsToDataArray(data?.ctg_data?.[id])
		const submitData = dataToSubmit?.override?.[id]
		const isDirty = dirtyFields?.override?.[id]
		return <div className="detail-connex__field-group-row">
			<div>
				<SelectLabel id={id}>{title}</SelectLabel>
				<FieldGroupArray
					id={id}
					fieldMap={fields}
					sourceData={overrideData}
					submitData={submitData}
					setDataToSubmit={setDataToSubmit}
					readOnly={onlyAttachment === "true"}
					isDirty={isDirty}
					label={title}
				/>
			</div>
			<div>
				<SelectLabel id={id}>{title}</SelectLabel>
				<FieldGroupArray
					id={id}
					fieldMap={fields}
					sourceData={origData}
					readOnly={true}
				/>
			</div>
		</div>
	}

	return <>
		<div className="detail-connex__panel">
			<div className="detail-connex__panel-header">Trial Overview</div>
			{fieldHeaderRow()}
			{getTextRow('textInput', 'brief_title', 'Brief Title')}
			{getTextRow('textInput', 'official_title', 'Official Title')}
			{getTextRow('textArea', 'brief_summary', 'Brief Summary', { rows: 6 })}
			{getSelectRow('overall_status', 'Recruitment Status', getRecruitStatusOptions())}
			<div style={{ paddingBottom: 22 }} />
		</div>

		<div className="detail-connex__panel">
			<div className="detail-connex__panel-header">Trial at a Glance</div>
			{fieldHeaderRow()}
			{getSelectCreatableRow('conditions', 'Conditions', conditionOptions, true)}
			{getSelectCreatableRow('interventions', 'Interventions', interventionOptions, true)}
			{getTextRow('textInput', 'enrollment', 'Enrollment')}
			{getDatePickerRow('start_date', 'Start Date')}
			{getDatePickerRow('completion_date', 'Completion Date')}
			<div style={{ paddingBottom: 22 }} />
		</div>

		<div className="detail-connex__panel">
			<div className="detail-connex__panel-header">Key Requirements</div>
			{fieldHeaderRow()}
			{getSelectRow('gender', 'Sex', getSexOptions())}
			{getSelectAge('maximum_age', 'Maximum Age')}
			{getSelectAge('minimum_age', 'Minimum Age')}
			{getToggle('accept_healthy_volunteers')}
			<div style={{ paddingBottom: 22 }} />
		</div>

		<div className="detail-connex__panel">
			<div className="detail-connex__panel-header">Trial Details / Participant Requirements</div>
			{fieldHeaderRow()}
			{getSelectRow('trial_phase', 'Trial Phase', getTrialPhaseOptions())}
			{getSelectRow('trial_type', 'Trial Type', getTrialTypeOptions())}
			{getTextRow("textInput", "trial_design", "Trial Design")}
			<div style={{ paddingBottom: 22 }} />
		</div>

		<div className="detail-connex__panel">
			<div className="detail-connex__panel-header">Trial Results</div>
			{fieldHeaderRow()}
			{getDatePickerRow('results_first_posted', 'Results Posted')}
			<div style={{ paddingBottom: 8 }} />
			<div className="detail-connex__field-group">
				{getFieldGroupArray(
					'trial_arms_group',
					'Trial Arms Group',
					trialArmsFields,
				)}
				{getFieldGroupArray(
					'primary_outcome_measures',
					'Primary Outcome Measures',
					outcomeMeasureFields,
				)}
				{getFieldGroupArray(
					'secondary_outcome_measures',
					'Secondary Outcome Measures',
					outcomeMeasureFields,
				)}
			</div>
			{getTextRow('textArea', 'eligibility_criteria', 'Eligibility Criteria', { rows: 10 })}
			<div style={{ paddingBottom: 22 }} />
		</div>
	</>
}

export default DetailConneX
